import { ASSESSMENTS, COMPLIANCE_REQUIREMENTS, CURRENT, DASHBOARDS, DETAILS, FFIEC_CAT, HISTORY, OPERATIONAL_CONTROLS, OVERALL, SCHEDULE, SUMMARY, TARGET_MATURITY, TPRM, UCRM } from 'Config/Paths';
import { AssessmentsResponse, ComplianceDetails, ComplianceDetailsSnapshot, ComplianceHistoryFFIECCAT, ComplianceSummary, ComplianceSummarySnapshot, ControlStrengthResponse, ControlsSnapshot, DueDiligenceDashboardCompletedRiskAssessment, DueDiligenceDashboardMonthlyRiskPortfolioSummary, MonthNumber, MonthlyEnabledControlsSnapshot, SetTargetMaturityFFIECCATRequest } from 'Models/Dashboards';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { restApiGet, restApiPost } from 'RestApiConnector';

import { DashboardApi } from './DashboardApi';

const dueDiligenceModulePathSegment = (module: DueDiligenceModule): string => (module === ProductModule.UCRM ? UCRM : TPRM);

export class AWSDashboardApi implements DashboardApi {
    getCompletedRiskAssessments = (module: DueDiligenceModule, startTime: string, endTime: string): Promise<ResponseModel<DueDiligenceDashboardCompletedRiskAssessment[]>> => {
        return restApiGet(`/${DASHBOARDS}/${dueDiligenceModulePathSegment(module)}/${ASSESSMENTS}`, { start_time: startTime, end_time: endTime });
    };

    getRiskPortfolioHistory = (module: DueDiligenceModule, startTime: string, endTime: string): Promise<ResponseModel<DueDiligenceDashboardMonthlyRiskPortfolioSummary[]>> => {
        return restApiGet(`/${DASHBOARDS}/${dueDiligenceModulePathSegment(module)}/${HISTORY}`, { start_time: startTime, end_time: endTime });
    };

    getControlStrength = (): Promise<ResponseModel<ControlStrengthResponse>> => {
        return restApiGet(`/${DASHBOARDS}/${OPERATIONAL_CONTROLS}/${SUMMARY}`, {});
    };

    getCurrentComplianceSummary = (): Promise<ResponseModel<ComplianceSummary[]>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${CURRENT}/${SUMMARY}`, {});
    };

    getCurrentComplianceDetails = (regulation: string): Promise<ResponseModel<ComplianceDetails>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${CURRENT}/${DETAILS}/${regulation}`, {});
    };

    getControlsHistory = (startTime: string, endTime: string): Promise<ResponseModel<ControlsSnapshot[]>> => {
        return restApiGet(`/${DASHBOARDS}/${OPERATIONAL_CONTROLS}/${HISTORY}`, { start_time: startTime, end_time: endTime });
    };

    getMonthlyEnabledControlSnapshots = (startTime: string, endTime: string): Promise<ResponseModel<MonthlyEnabledControlsSnapshot[]>> => {
        return restApiGet(`/${DASHBOARDS}/${OPERATIONAL_CONTROLS}/${HISTORY}/${OVERALL}`, { start_time: startTime, end_time: endTime });
    };

    getComplianceHistorySummary = (regulation: string): Promise<ResponseModel<ComplianceSummarySnapshot[]>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${HISTORY}/${regulation}`, {});
    };

    getComplianceHistoryDetails = (regulation: string, month: MonthNumber, year: number): Promise<ResponseModel<ComplianceDetailsSnapshot>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${HISTORY}/${regulation}/${DETAILS}`, { month, year });
    };

    getComplianceHistorySummaryFFIECCAT = (regulation: string, maturityLevel: string): Promise<ResponseModel<ComplianceHistoryFFIECCAT>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${HISTORY}/${regulation}`, { target_maturity: maturityLevel });
    };

    getComplianceHistoryDetailsFFIECCAT = (regulation: string, month: MonthNumber, year: number, maturityLevel: string): Promise<ResponseModel<ComplianceDetailsSnapshot>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${HISTORY}/${regulation}/${DETAILS}`, { month, year, target_maturity: maturityLevel });
    };

    getAssessments = (): Promise<ResponseModel<AssessmentsResponse>> => {
        return restApiGet(`/${DASHBOARDS}/${OPERATIONAL_CONTROLS}/${ASSESSMENTS}/${SCHEDULE}`, {});
    };

    getTargetMaturity = (): Promise<ResponseModel<string>> => {
        return restApiGet(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${FFIEC_CAT}/${TARGET_MATURITY}`, {});
    };

    setTargetMaturity = (request: SetTargetMaturityFFIECCATRequest): Promise<ResponseModel<void>> => {
        return restApiPost(`/${DASHBOARDS}/${COMPLIANCE_REQUIREMENTS}/${FFIEC_CAT}/${TARGET_MATURITY}`, request);
    };
}
